import { BaseModel } from '../../base.model';

export enum MergeStatus {
  initial = 'initial',
  queued = 'queued',
  inProgress = 'inProgress',
  succeeded = 'succeeded',
  failed = 'failed',
}

/**
 * Execution phases are used to:
 * - report progress,
 * - pinpoint failures,
 * - enforce Firestore -> Auth -> Cleanup ordering.
 */
export enum MergePhase {
  firestoreMerge = 'firestoreMerge',
  auth = 'auth',
  cleanup = 'cleanup',
}

export enum MergeErrorDetailsKeys {
  code = 'code',
  message = 'message',
  phase = 'phase',
}

export interface MergeErrorDetails {
  /** Stable-ish identifier for dashboards/alerts. Example: `FIRESTORE_PRECONDITION_FAILED`. */
  code: string;
  /** Human-readable message safe to show to admins. */
  message: string;
  /** Where the error happened in the merge lifecycle. */
  phase: MergePhase;
}

/**
 * Lease/ownership for safe concurrent processing.
 * Only the worker holding an active lease is allowed to mutate the request and execute it.
 */
export interface MergeExecutionLease {
  /** Identifier of the worker instance (or Cloud Task name) currently processing. */
  ownerId: string;
  /** Worker must renew before this time; otherwise another worker can take the lease. */
  expiresAt: Date;
}

/**
 * Primary (top-level) field merge decisions taken in UI.
 * Mirrors the current Agent Merge Tool behavior: choose target/source/custom for each field.
 */
export interface AgentMergeFieldDecision {
  /** Agent document field key (usually from `AgentKeys.*`). */
  key: string;
  /** How to compute the merged value. */
  action: AgentMergeFieldAction;
  /** Required only for `custom`. */
  customValue?: unknown;
  /**
   * Some UI rows are "composed" and write multiple fields together (e.g. MGA hierarchy).
   * The backend uses this to apply the decision consistently.
   */
  composedKeys?: string[];
  /**
   * UI can mark a decision as skipped due to a validation prompt (e.g. "Continue without MGA").
   * Backend should re-run validation and may still enforce skipping.
   */
  skip?: boolean;
}

export enum AgentMergeFieldAction {
  useTarget = 'targetValue',
  useSource = 'sourceValue',
  useSourceIfExists = 'existSourceValue',
  custom = 'custom',
}

export enum MergeItemAction {
  create = 'create',
  update = 'update',
  delete = 'delete',
}

/**
 * Collection-like section actions coming from UI grids:
 * - items moved from source to target are typically `create`
 * - conflicts resolved over an existing target item become `update`
 */
export interface MergeItemPatch {
  action: MergeItemAction;
  /** Existing doc id when updating (or when creating with a known id). */
  id?: string;
  /**
   * Optional source doc id to copy from (or to read "before" data from) when applying the operation.
   * Useful for agent subcollections where we want to send only ids, not full document bodies.
   */
  sourceId?: string;
  /**
   * Patch payload. Prefer storing only fields needed for the update, not a full document clone.
   * Backend must validate allowed keys.
   */
  data?: Record<string, unknown>;
}

export interface AgentMergeSectionsPatch {
  /** Plain sections (stored on the agent doc). */
  addresses?: unknown[];
  phoneNumbers?: unknown[];
  socials?: unknown[];
  websites?: unknown[];
  carriers?: unknown[];
  statesLicensedIn?: unknown[];

  /** Collection-based sections (stored as subcollections or separate collections). */
  emailAddresses?: MergeItemPatch[];
  associations?: MergeItemPatch[];
  conferences?: MergeItemPatch[];
  media?: MergeItemPatch[];
  notes?: MergeItemPatch[];
  meetings?: MergeItemPatch[];
  calls?: MergeItemPatch[];
  mailLogs?: MergeItemPatch[];
  tasks?: MergeItemPatch[];
  needToKnow?: MergeItemPatch[];
  statusActivities?: MergeItemPatch[];
}

/**
 * Client-provided merge plan
 */
export interface AgentMergePatch {
  /** Field-level decisions for the main agent document. */
  fieldDecisions: AgentMergeFieldDecision[];
  /** Section-specific decisions for related data. */
  sections: AgentMergeSectionsPatch;
}

/**
 * Auth is executed last (after Firestore merge succeeds) to reduce the chance of breaking login.
 * We store both the requested intent and backend-recorded compensation data.
 */
export interface MergeAuthPatch {
  /**
   * Selected login identity for the final agent.
   * Client typically provides `selectedUid` (and optionally email/emailVerified) from the merge UI.
   */
  selectedUid: string | null;

  /**
   * Backend-computed: which uid is safe to delete after binding (or which source uid to delete).
   * Stored here for transparency and idempotency across retries.
   */
  uidToDelete?: string | null;
}

export interface MergeAuthState {
  /**  Client-provided selection */
  patch: MergeAuthPatch;
}

export interface MergeExecutionResult {
  /** What changed in Firestore (for audit + debugging). */
  firestore: {
    createdCount: number;
    updatedCount: number;
    deletedCount: number;
    /**
     * Optional sampled list of doc paths touched.
     * Keep small to avoid doc size bloat; detailed ops go to subcollection.
     */
    touchedPaths?: string[];
  };
  /** Result of Auth-last step. */
  auth?: {
    selectedUid: string | null;
    uidDeleted?: string | null;
    claimsUpdated: boolean;
    skipped?: boolean;
  };
}

export enum MergeExecutionStateKeys {
  status = 'status',
  phase = 'phase',
  attempt = 'attempt',
  startedAt = 'startedAt',
  finishedAt = 'finishedAt',
  updatedAt = 'updatedAt',
}

export interface MergeExecutionState {
  status: MergeStatus;
  phase: MergePhase;
  /** Backend increments on each attempt (including retries). */
  attempt: number;

  startedAt: Date | null;
  finishedAt: Date | null;
  updatedAt: Date;
}

export enum AgentMergeRequestModelKeys {
  targetAgentDbId = 'targetAgentDbId',
  sourceAgentDbId = 'sourceAgentDbId',
  targetAgentPrimaryEmail = 'targetAgentPrimaryEmail',
  sourceAgentPrimaryEmail = 'sourceAgentPrimaryEmail',
  resultAgentPrimaryEmail = 'resultAgentPrimaryEmail',
  targetAgentFullName = 'targetAgentFullName',
  sourceAgentFullName = 'sourceAgentFullName',
  createdByAgentFullName = 'createdByAgentFullName',
  resultAgentFullName = 'resultAgentFullName',
  patch = 'patch',
  auth = 'auth',
  execution = 'execution',
  lease = 'lease',
  result = 'result',
  error = 'error',
}

/**
 * Firestore document: `merge-requests/{requestId}`
 */
export interface AgentMergeRequestModel extends BaseModel {
  /** Immutable merge identity. */
  targetAgentDbId: string;
  sourceAgentDbId: string;
  targetAgentPrimaryEmail: string | null;
  sourceAgentPrimaryEmail: string | null;
  resultAgentPrimaryEmail: string | null;
  targetAgentFullName: string | null;
  sourceAgentFullName: string | null;
  createdByAgentFullName: string | null;
  resultAgentFullName: string | null;

  /**
   * Client-provided merge decisions (what to keep/move/update).
   * Backend validates and executes this plan.
   */
  patch: AgentMergePatch;

  /** Auth intent (executed last by backend). */
  auth: MergeAuthState;

  /**
   * Backend-controlled execution state.
   * Client should never write these fields (except maybe `status=canceled` if you support cancelation).
   */
  execution: MergeExecutionState;
  lease?: MergeExecutionLease | null;

  /** Backend-controlled outputs. */
  result?: MergeExecutionResult | null;
  error?: MergeErrorDetails | null;
}
